document.addEventListener('DOMContentLoaded', () => {
    const cartContainer = document.getElementById('main-cart-container');
    let syncTimeout = null;
    const pendingUpdates = new Map(); // Track pending quantity updates by item key
    const syncingKeys = new Set(); // Track items actively syncing (blocked)

    // Instantly update UI (optimistic update) with loading indicators
    function updateUIInstantly(key, newQty, triggerElement) {
        const row = triggerElement?.closest('.flex.flex-col');
        if (!row) return;

        // Update quantity display with pulse animation
        const qtyDisplay = row.querySelector('.cart-qty-display');
        if (qtyDisplay) {
            qtyDisplay.textContent = newQty;
            qtyDisplay.classList.add('qty-syncing'); // Add pulse animation
        }

        // Show spinner next to quantity
        const spinner = row.querySelector('.cart-qty-spinner');
        if (spinner) spinner.classList.remove('hidden');

        // Update the data-qty attributes on buttons
        const btns = row.querySelectorAll('.qty-adjust');
        if (btns.length >= 2) {
            btns[0].dataset.qty = Math.max(0, newQty - 1);
            btns[1].dataset.qty = newQty + 1;
        }

        // Show loading spinner on line item price (hide price, show spinner)
        const lineLoader = row.querySelector('.line-price-loader');
        const linePrice = row.querySelector('.line-price');
        if (lineLoader) lineLoader.classList.remove('hidden');
        if (linePrice) linePrice.classList.add('hidden');

        // Store pending update
        pendingUpdates.set(key, newQty);

        // Optimistically calculate estimated new subtotal across all cart rows
        let newTotal = 0;
        const allRows = document.querySelectorAll('#main-cart-container [data-item-key]');
        allRows.forEach((r) => {
            const uPrice = parseInt(r.dataset.unitPrice, 10) || 0;
            const rKey = r.dataset.itemKey;
            const q = pendingUpdates.has(rKey) ? pendingUpdates.get(rKey) : (parseInt(r.querySelector('.cart-qty-display')?.textContent, 10) || 0);
            newTotal += uPrice * q;
        });

        const formatPrice = (cents) => typeof window.formatMoney === 'function' ? window.formatMoney(cents) : `Rs. ${(cents / 100).toFixed(2)}`;

        // Optimistically update subtotal and total texts
        const subtotal = document.getElementById('cart-subtotal');
        const total = document.getElementById('cart-total');
        const stickyTotal = document.getElementById('StickyCartTotal');
        
        if (subtotal) { subtotal.textContent = formatPrice(newTotal); subtotal.style.opacity = '0.7'; }
        if (total) { total.textContent = formatPrice(newTotal); total.style.opacity = '0.7'; }
        if (stickyTotal) { stickyTotal.textContent = formatPrice(newTotal); stickyTotal.style.opacity = '0.7'; }


    }

    // Block controls during sync
    function blockControlsForKey(key) {
        const controls = document.querySelector(`.cart-qty-controls[data-item-key="${key}"]`);
        if (controls) {
            controls.style.transition = 'opacity 0.3s ease';
            controls.style.opacity = '0.5';
            controls.style.pointerEvents = 'none';
            controls.querySelectorAll('button').forEach((btn) => {
                btn.style.transition = 'filter 0.3s ease, opacity 0.3s ease';
                btn.style.filter = 'grayscale(100%)';
                btn.style.opacity = '0.6';
            });
        }
    }

    // Sync with server after user stops clicking (debounced)
    let isRefreshing = false;
    
    function scheduleSyncWithServer() {
        if (syncTimeout) clearTimeout(syncTimeout);

        syncTimeout = setTimeout(async () => {
            if (pendingUpdates.size === 0 || isRefreshing) return;
            isRefreshing = true;

            // 🔒 Block all pending items
            for (const [key] of pendingUpdates) {
                syncingKeys.add(key);
                blockControlsForKey(key);
            }

            try {
                // Process all pending updates
                for (const [key, qty] of pendingUpdates) {
                    const response = await fetch('/cart/change.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id: key, quantity: qty }),
                    });
                    if (!response.ok) throw new Error('Network response was not ok');
                }

                // Clear pending before refresh
                const keysToUnblock = [...pendingUpdates.keys()];
                pendingUpdates.clear();

                // Fetch updated section HTML using Section Rendering API
                const sectionResponse = await fetch(`${window.location.pathname}?sections=main-cart`);
                if (!sectionResponse.ok) throw new Error('Failed to fetch section');
                const sectionData = await sectionResponse.json();

                if (sectionData['main-cart']) {
                    const parser = new DOMParser();
                    const newDoc = parser.parseFromString(sectionData['main-cart'], 'text/html');
                    const newContainer = newDoc.getElementById('main-cart-container');

                    if (newContainer && cartContainer) {
                        const currentScrollY = window.scrollY || document.documentElement.scrollTop;
                        const currentNoteEl = document.getElementById('CartNote');
                        const activeNoteVal = currentNoteEl ? currentNoteEl.value : null;

                        cartContainer.innerHTML = newContainer.innerHTML;
                        attachCartListeners();

                        if (activeNoteVal !== null && document.getElementById('CartNote')) {
                            document.getElementById('CartNote').value = activeNoteVal;
                        }

                        // Restore exact scroll position to prevent mobile scroll jumping
                        window.scrollTo(0, currentScrollY);
                        requestAnimationFrame(() => {
                            window.scrollTo(0, currentScrollY);
                        });
                    }
                }

                // Update header cart indicator
                if (window.EngoodCart) {
                    const cart = await window.EngoodCart.getCart();
                    window.EngoodCart.syncCards(cart);
                }

                // 🔓 Clear syncing keys
                keysToUnblock.forEach((key) => syncingKeys.delete(key));
                isRefreshing = false;
            } catch (error) {
                console.error('Error syncing cart:', error);
                // Clear syncing keys even on error
                syncingKeys.clear();
                
                // Show error toast (C2)
                const toast = document.createElement('div');
                toast.className = 'fixed top-4 right-4 md:top-6 md:right-6 bg-red-600 text-white px-5 py-3 rounded-lg shadow-xl z-[200] font-bold flex items-center gap-2 animate-[pulse_1s_ease-in-out_infinite]';
                toast.innerHTML = '<span class="material-symbols-outlined">error</span> Sync failed. Refreshing cart...';
                document.body.appendChild(toast);
                
                // On error, reload to get correct state
                setTimeout(() => location.reload(), 2500);
            }
        }, 800); // 0.8 second debounce for final sync
    }

    // Handle quantity change
    function handleQuantityChange(key, newQty, triggerElement) {
        // 🛡️ GUARD: If item is syncing, ignore click
        if (syncingKeys.has(key)) {
            console.log('Blocked - item is syncing');
            return;
        }

        if (newQty < 0) return;

        // 🛡️ GUARD: Max quantity validation (M5)
        const row = triggerElement?.closest('[data-max]');
        const max = row ? parseInt(row.dataset.max, 10) : 99;
        if (newQty > max) {
            if (window.EngoodCart && typeof window.EngoodCart.showMaxLimitToast === 'function') {
                window.EngoodCart.showMaxLimitToast(max);
            }
            return;
        }

        if (newQty === 0) {
            // Remove item - fade out row
            const row = triggerElement?.closest('.flex.flex-col');
            if (row) {
                row.style.transition = 'opacity 0.3s, transform 0.3s';
                row.style.opacity = '0';
                row.style.transform = 'translateX(-20px)';
            }
        }

        updateUIInstantly(key, newQty, triggerElement);
        scheduleSyncWithServer();
    }

    // Attach event listeners
    function attachCartListeners() {
        document.querySelectorAll('.qty-adjust').forEach((btn) => {
            // Remove old listeners by cloning
            const newBtn = btn.cloneNode(true);
            btn.parentNode.replaceChild(newBtn, btn);

            newBtn.addEventListener('click', (e) => {
                e.preventDefault();
                const key = newBtn.dataset.key;
                const qty = parseInt(newBtn.dataset.qty);
                handleQuantityChange(key, qty, newBtn);
            });
        });

        document.querySelectorAll('.cart-remove').forEach((btn) => {
            const newBtn = btn.cloneNode(true);
            btn.parentNode.replaceChild(newBtn, btn);

            newBtn.addEventListener('click', (e) => {
                e.preventDefault();
                const key = newBtn.dataset.key;
                const title = newBtn.dataset.title || 'Item';
                const row = newBtn.closest('.flex.flex-col.sm\\:flex-row') || newBtn.closest('[data-item-key]');

                // H2: Use undo toast instead of instant remove
                if (window.EngoodCart && row) {
                    window.EngoodCart.showUndoToast(key, title, 1, row, newBtn);
                } else {
                    // Fallback if EngoodCart not ready
                    handleQuantityChange(key, 0, newBtn);
                }
            });
        });
    }

    // Sticky Mobile Checkout Logic
    function initStickyCheckout() {
        const stickyBar = document.getElementById('MobileStickyCheckout');
        const mainCheckoutBtn = document.getElementById('main-checkout-btn');
        
        if (!stickyBar || !mainCheckoutBtn) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // Show sticky bar only on mobile and when main checkout is NOT visible
                    if (!entry.isIntersecting && window.innerWidth < 1024) {
                        stickyBar.classList.remove('translate-y-full');
                    } else {
                        stickyBar.classList.add('translate-y-full');
                    }
                });
            },
            { threshold: 0 }
        );
        
        observer.observe(mainCheckoutBtn);
        
        // Hide on resize if desktop
        window.addEventListener('resize', () => {
            if (window.innerWidth >= 1024) {
                stickyBar.classList.add('translate-y-full');
            }
        });
    }

    // 🚀 FIX: Cart Page Race Condition (Sticky Checkout vs. Debounced Quantity Sync)
    const cartForm = document.getElementById('cart-form');
    if (cartForm) {
        cartForm.addEventListener('submit', async (e) => {
            // If user clicks Checkout but there are pending AJAX quantity updates
            if (pendingUpdates.size > 0 || isRefreshing) {
                e.preventDefault(); // Stop the checkout redirect

                if (syncTimeout) clearTimeout(syncTimeout); // Cancel the delayed sync
                
                // Show loading state on the checkout button
                const btn = e.submitter || cartForm.querySelector('[name="checkout"]');
                if (btn) {
                    btn.classList.add('opacity-70', 'pointer-events-none');
                    btn.innerHTML = '<span class="material-symbols-outlined animate-spin text-[18px]">sync</span><span>Syncing...</span>';
                }

                try {
                    // Force flush all pending quantities to Shopify synchronously
                    for (const [key, qty] of pendingUpdates) {
                        await fetch('/cart/change.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ id: key, quantity: qty }),
                        });
                    }
                    pendingUpdates.clear();
                    
                    // Proceed to checkout safely with updated cart data
                    HTMLFormElement.prototype.submit.call(cartForm);
                } catch (error) {
                    console.error('Checkout sync failed', error);
                    window.location.reload(); // Fallback to reload if error
                }
            }
        });
    }

    initStickyCheckout();
    attachCartListeners();

    // Gift Note auto-save via event delegation (persists across innerHTML refreshes)
    let noteDebounceTimer = null;
    function saveCartNote(value) {
        if (window.EngoodCart && typeof window.EngoodCart.saveNote === 'function') {
            window.EngoodCart.saveNote(value);
            return;
        }
        fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: value })
        }).catch(e => console.error('Error saving cart note:', e));
    }

    document.addEventListener('input', (e) => {
        if (e.target && e.target.id === 'CartNote') {
            if (noteDebounceTimer) clearTimeout(noteDebounceTimer);
            noteDebounceTimer = setTimeout(() => saveCartNote(e.target.value), 500);
        }
    });

    document.addEventListener('blur', (e) => {
        if (e.target && e.target.id === 'CartNote') {
            if (noteDebounceTimer) clearTimeout(noteDebounceTimer);
            saveCartNote(e.target.value);
        }
    }, true);
});